import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import { Chip } from '@mui/material';
import Appbar from 'components/appbar/Appbar';
import ProcedureForm from '../form/ProcedureForm';
import { useProcedureValidation } from '../validation/useProcedureValidation';
import ProcedureTab from '../tab/ProcedureTab';
import { api } from 'services/api';
import { toast } from 'react-toastify';
import history from 'services/history';
import InsideSaving from 'components/loading/InsideSaving';
import { useParams } from 'react-router-dom';
import InsideLoading from 'components/loading/InsideLoading';
import NoData from 'components/no-data/NoData';
import { percentFormat } from 'helpers/moneyFormat';
import { Procedure, Anesthetist, ProcedureDoctor, ProcedureProduct, ProcedurePackage } from 'types/procedure';
import ProcedureProducts from '../ProcedureProducts';
import ProcedureDoctors from '../ProcedureDoctors';
import ProcedureAnesthetistForm from '../form/ProcedureAnesthetistForm';
import ProcedureSpecialtiesForm from '../form/ProcedureSpecialtiesForm';
import { Specialty } from 'types/specialty';
import ProcedurePackages from '../ProcedurePackages';
import {
  ContentPad,
  PageShell,
  RecordActions,
  RecordHeader,
  RecordNote,
  RecordSection,
  StyledForm,
  WorkArea,
} from 'components/record-shell/RecordShell';

type ItemKey = 'doctors' | 'packages' | 'products';

// Só o que a barra "Salvar" grava: serve para saber se há alterações pendentes.
function snapshot(procedure: Procedure | null, anesthetist: Anesthetist | null) {
  if (!procedure) return '';

  return JSON.stringify({
    name: procedure.name,
    profit_margin: Number(procedure.profit_margin),
    active: Number(procedure.active),
    specialties: (procedure.specialties || []).map(item => [item.specialty_id, Number(item.active)]).sort(),
    anesthetist: [Number(anesthetist?.sale_price_min) || 0, Number(anesthetist?.sale_price_max) || 0],
  });
}

const EditProcedure: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [validation, setValidation, validate] = useProcedureValidation();
  const [tab, setTab] = useState('main');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingSpecialties, setLoadingSpecialties] = useState(false);
  const [specialties, setSpecialties] = useState<Specialty[]>([]);
  const [procedure, setProcedure] = useState<Procedure | null>(null);
  // como o procedimento está gravado: as gravações de médicos, pacotes e produtos partem daqui, sem levar junto o que ainda não foi salvo
  const [saved, setSaved] = useState<Procedure | null>(null);
  const [anesthetist, setAnesthetist] = useState<Anesthetist | null>(null);
  const [savedSnapshot, setSavedSnapshot] = useState('');

  const dirty = useMemo(
    () => snapshot(procedure, anesthetist) !== savedSnapshot,
    [procedure, anesthetist, savedSnapshot],
  );

  // nome e margem ficam na aba Geral: abre ela quando a validação reclama de um deles
  useEffect(() => {
    if (Object.keys(validation).length) setTab('main');
  }, [validation]);

  function handleChangeAnesthetist<K extends keyof Anesthetist>(index: K, value: Anesthetist[K]) {
    setAnesthetist(prev => (prev ? { ...prev, [index]: value } : null));
  }

  useEffect(() => {
    setLoadingSpecialties(true);

    api
      .get('/specialties/all')
      .then(response => {
        setSpecialties(response.data);
      })
      .catch(err => {
        console.error(err);
        toast.error('Não foi possível carregar as especialidades');
      })
      .finally(() => setLoadingSpecialties(false));
  }, []);

  useEffect(() => {
    if (!id) return;
    setLoading(true);

    api
      .get(`/procedures/${id}`)
      .then(response => {
        const procedureData = response.data;
        const loaded: Procedure = {
          ...procedureData,
          type: procedureData.type || 'P',
          // a API exige número; cadastros antigos podem vir sem esses valores
          sale_price: procedureData.sale_price ?? 0,
          cost: procedureData.cost ?? 0,
          commission: procedureData.commission ?? 0,
          specialties: procedureData.specialties.map(item => ({ ...item, active: 1 })) || [],
        };
        const loadedAnesthetist = procedureData.anesthetist || { sale_price_max: 0, sale_price_min: 0 };

        setProcedure(loaded);
        setSaved(loaded);
        setAnesthetist(loadedAnesthetist);
        setSavedSnapshot(snapshot(loaded, loadedAnesthetist));
      })
      .catch(err => {
        console.error(err);
        toast.error('Não foi possível carregar os dados do procedimento');
        history.push('/procedures');
      })
      .finally(() => setLoading(false));
  }, [id]);

  function handleChangeSpecialties(selectedIds: number[]) {
    if (!procedure) return;

    // `selectedIds` são os ids escolhidos; o procedimento guarda o vínculo (id) e se está ativo.
    // Vínculos já existentes mantêm o id, novos entram com id 0 e os removidos ficam com active 0.
    const current = procedure.specialties || [];

    const addedOrUpdated = selectedIds.map(sid => {
      const existing = current.find(item => item.specialty_id === sid);

      return existing
        ? { ...existing, active: 1 }
        : { id: 0, specialty_id: sid, active: 1, specialty: specialties.find(s => s.id === sid) || null };
    });
    const removed = current
      .filter(item => !selectedIds.includes(item.specialty_id))
      .map(item => ({ ...item, active: 0 }));

    setProcedure(prev => (prev ? { ...prev, specialties: [...addedOrUpdated, ...removed] } : null));
  }

  function handleChange<K extends keyof Procedure>(index: K, value: Procedure[K]) {
    setProcedure(prev => (prev ? { ...prev, [index]: value } : null));
  }

  function handleSubmit() {
    if (!procedure) return;

    setSaving(true);

    api
      .put(`/procedures/${procedure.id}`, {
        ...procedure,
        anesthetist,
      })
      .then(() => {
        toast.success('Procedimento atualizado com sucesso!');
        history.push('/procedures');
      })
      .catch(err => {
        toast.error(err.response?.data?.error || 'Não foi possível atualizar o procedimento');
      })
      .finally(() => setSaving(false));
  }

  function handleValidation(e?: FormEvent<HTMLFormElement>) {
    e?.preventDefault();
    if (!procedure) return;

    validate(procedure)
      .then(() => {
        handleSubmit();
        setValidation({});
      })
      .catch(() => {
        // o erro já foi guardado em `validation`
      });
  }

  // Médicos, pacotes e produtos são gravados na hora, a partir do que já está salvo.
  function saveItem(
    key: ItemKey,
    item: (ProcedureDoctor | ProcedurePackage | ProcedureProduct) & { active?: number },
    errorMessage: string,
  ) {
    if (!saved) return;

    api
      .put(`/procedures/${saved.id}`, { ...saved, [key]: [item] })
      .then(response => {
        toast.success('Alteração salva');
        setProcedure(state => (state ? { ...state, [key]: response.data[key] } : null));
        setSaved(state => (state ? { ...state, [key]: response.data[key] } : null));
      })
      .catch(err => {
        toast.error(err.response?.data?.error || errorMessage);
      });
  }

  const handleChangeAddProduct = (product: ProcedureProduct) =>
    saveItem('products', product, 'Não foi possível adicionar o produto ao procedimento');
  const handleDeleteProduct = (product: ProcedureProduct) =>
    saveItem('products', { ...product, active: 0 }, 'Não foi possível excluir o produto do procedimento');
  const handleChangeAddPackage = (pkg: ProcedurePackage) =>
    saveItem('packages', pkg, 'Não foi possível adicionar o pacote ao procedimento');
  const handleDeletePackage = (pkg: ProcedurePackage) =>
    saveItem('packages', { ...pkg, active: 0 }, 'Não foi possível excluir o pacote do procedimento');
  const handleChangeAddDoctor = (doctor: ProcedureDoctor) =>
    saveItem('doctors', doctor, 'Não foi possível adicionar o médico ao procedimento');
  const handleDeleteDoctor = (doctor: ProcedureDoctor) =>
    saveItem('doctors', { ...doctor, active: 0 }, 'Não foi possível excluir o médico do procedimento');

  const counts = {
    doctors: procedure?.doctors?.length || 0,
    specialties: procedure?.specialties?.filter(item => item.active !== 0).length || 0,
    packages: procedure?.packages?.length || 0,
    products: procedure?.products?.length || 0,
  };
  const isItemTab = tab === 'doctors' || tab === 'packages' || tab === 'products';

  function goBack() {
    history.push('/procedures');
  }

  return (
    <>
      <Appbar title="Procedimentos" backAction={goBack} />

      {saving && <InsideSaving />}

      <PageShell>
        <RecordHeader title={saved?.name || 'Procedimento'} active={!!saved?.active} backAction={goBack}>
          <Chip
            size="small"
            variant="outlined"
            color="primary"
            label={`Margem ${percentFormat(saved?.profit_margin || 0)}`}
          />
        </RecordHeader>

        <WorkArea>
          <ProcedureTab onChange={newTab => setTab(newTab)} tab={tab} counts={counts} />
          <ContentPad>
            {loading ? (
              <InsideLoading />
            ) : !procedure ? (
              <NoData message="Não foi possível carregar os dados do procedimento" />
            ) : (
              <StyledForm onSubmit={handleValidation}>
                {tab === 'packages' ? (
                  <ProcedurePackages
                    procedure={procedure}
                    handleDeletePackage={handleDeletePackage}
                    handleChangeAddPackage={handleChangeAddPackage}
                  />
                ) : tab === 'products' ? (
                  <ProcedureProducts
                    procedure={procedure}
                    handleDeleteProduct={handleDeleteProduct}
                    handleChangeAddProduct={handleChangeAddProduct}
                  />
                ) : tab === 'doctors' ? (
                  <ProcedureDoctors
                    procedure={procedure}
                    handleDeleteDoctor={handleDeleteDoctor}
                    handleChangeAddDoctor={handleChangeAddDoctor}
                  />
                ) : tab === 'specialties' ? (
                  <ProcedureSpecialtiesForm
                    handleChangeSpecialties={handleChangeSpecialties}
                    procedure={procedure}
                    loadingSpecialties={loadingSpecialties}
                    specialties={specialties}
                  />
                ) : (
                  <>
                    <ProcedureForm handleChange={handleChange} procedure={procedure} validation={validation} />
                    <RecordSection title="Anestesista">
                      <ProcedureAnesthetistForm
                        anesthetist={anesthetist}
                        handleChangeAnesthetist={handleChangeAnesthetist}
                      />
                    </RecordSection>
                  </>
                )}
                {isItemTab && (
                  <RecordNote>Médicos, pacotes e produtos são salvos na hora, ao adicionar ou remover.</RecordNote>
                )}
                <button type="submit" style={{ display: 'none' }}></button>
              </StyledForm>
            )}
          </ContentPad>
        </WorkArea>

        {!isItemTab && !loading && procedure && (
          <RecordActions
            saving={saving}
            saveLabel="Salvar alterações"
            dirty={dirty}
            onSave={() => handleValidation()}
          />
        )}
      </PageShell>
    </>
  );
};

export default EditProcedure;
